import { ToastColorTypes } from '@insurancemenu/imenu-components-lib/dist/types';
import { makeAutoObservable, runInAction, toJS } from 'mobx';
import { RootStore } from 'store/rootStore';
import { ConfigurableEmailInfo } from 'types/emailService';
import { ToastInfo } from 'types/general';

export default class NotificationSettingsStore {
  rootStore: RootStore;
  loading = false;
  /**
   * Info with email type and description
   */
  emails: ConfigurableEmailInfo[] = [];
  /**
   * Emails that should be send to user
   */
  emailsForUser: string[] = [];
  /**
   * Toast Info
   */
  toastInfo: ToastInfo = {
    showToast: false,
    toastType: ToastColorTypes.SUCCESS,
    toastMessage: '',
  };

  private setLoading(loadingStatus: boolean): void {
    this.loading = loadingStatus;
  }

  private showToast(toastMessage: string, type: ToastColorTypes): void {
    this.resetToastInfo();
    this.toastInfo.showToast = true;
    this.toastInfo.toastMessage = toastMessage;
    this.toastInfo.toastType = type;
  }

  public closeToast(): void {
    this.toastInfo.showToast = false;
    this.resetToastInfo();
  }

  private resetToastInfo(): void {
    this.toastInfo.toastType = ToastColorTypes.SUCCESS;
    this.toastInfo.toastMessage = '';
  }

  public async getEmails(): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.emailStore.getConfigurableEmails();
    runInAction(() => {
      this.emails = res.emails;
      this.emailsForUser = res.emailsForUser;
      this.setLoading(false);
    });
  }

  public async saveConfigurableEmailsForUser(selectedEmails: string[]): Promise<void> {
    this.setLoading(true);
    const res = await this.rootStore.emailStore.saveConfigurableEmailsForUser(selectedEmails);
    await runInAction(async () => {
      if (res.data?.saveConfigurableEmailsForUser) {
        this.showToast('Email list updated successfully', ToastColorTypes.SUCCESS);
      } else {
        this.showToast(
          'Something went wrong while updating the email list. Please try again or contact our support team for assistance.',
          ToastColorTypes.WARNING,
        );
        // If an issue occurred while saving emails, refresh the email list from the server
        // to ensure it displays the latest values from the backend.
        await this.getEmails();
      }
      this.setLoading(false);
    });
  }

  public updateEmailCheckStatus(emailType: string): void {
    const convertedEmailsForUser = this.emailsForUser.map((type) => toJS(type)).slice();
    if (convertedEmailsForUser.includes(emailType)) {
      this.emailsForUser = convertedEmailsForUser.filter((type) => type !== emailType);
    } else {
      this.emailsForUser = [...convertedEmailsForUser, emailType];
    }
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(this, {}, { autoBind: true });
  }
}
